/**
 * Workflow trigger filters (`trigger.filters` on a DAG definition).
 *
 * The backend checks these against the flat event payload before starting a
 * run (EventBus._filters_match in catax_ai/workflow/event_bus.py). Every
 * filter must match. Stored shapes the backend accepts:
 *   "CL"                              legacy: equals
 *   ["CL", "MX"]                      legacy: is one of
 *   {"op": "not_in", "value": [...]}  structured (what the builder writes)
 */

export interface TriggerFilterRow { key: string; op: string; value: string }

export const FILTER_OP_LABELS: Record<string, string> = {
  eq: '= equals',
  neq: '≠ not equals',
  in: 'is one of',
  not_in: 'is not one of',
  contains: 'contains',
  not_contains: 'does not contain',
  gt: '>',
  lt: '<',
  gte: '>=',
  lte: '<=',
  truthy: 'is truthy',
  falsy: 'is falsy',
  exists: 'exists',
  not_exists: 'does not exist',
};

/** Operators whose value is a comma-separated list. */
export const LIST_FILTER_OPS = new Set(['in', 'not_in']);
/** Operators that take no value. */
export const VALUE_FREE_FILTER_OPS = new Set(['truthy', 'falsy', 'exists', 'not_exists']);

const isStructured = (v: unknown): v is { op: string; value?: unknown } =>
  !!v && typeof v === 'object' && !Array.isArray(v) && 'op' in (v as object);

/** Stored `trigger.filters` (any accepted shape) → editable rows. */
export function filtersToRows(filters?: Record<string, any> | null): TriggerFilterRow[] {
  return Object.entries(filters ?? {}).map(([key, v]) => {
    if (isStructured(v)) {
      const val = v.value;
      return { key, op: String(v.op), value: Array.isArray(val) ? val.join(', ') : val == null ? '' : String(val) };
    }
    if (Array.isArray(v)) return { key, op: 'in', value: v.join(', ') };
    return { key, op: 'eq', value: v == null ? '' : String(v) };
  });
}

/**
 * Editable rows → `trigger.filters`. Blank-field rows are dropped.
 * Throws an Error with a readable message for an incomplete or duplicate row.
 */
export function rowsToFilters(rows: TriggerFilterRow[]): Record<string, any> {
  const out: Record<string, any> = {};
  for (const r of rows) {
    const key = r.key.trim();
    if (!key) continue;
    if (key in out) {
      throw new Error(`Trigger filter "${key}" is listed twice. Use "is one of" for several values.`);
    }
    if (VALUE_FREE_FILTER_OPS.has(r.op)) {
      out[key] = { op: r.op };
    } else if (LIST_FILTER_OPS.has(r.op)) {
      const values = r.value.split(',').map(s => s.trim()).filter(Boolean);
      if (values.length === 0) throw new Error(`Trigger filter "${key}" needs at least one value.`);
      out[key] = { op: r.op, value: values };
    } else {
      const value = r.value.trim();
      if (!value) throw new Error(`Trigger filter "${key}" needs a value.`);
      out[key] = { op: r.op, value };
    }
  }
  return out;
}

const SHORT_OP: Record<string, string> = {
  eq: '=', neq: '≠', in: 'in', not_in: 'not in', contains: 'contains', not_contains: 'not contains',
  gt: '>', lt: '<', gte: '≥', lte: '≤', truthy: 'is set', falsy: 'is empty', exists: 'exists', not_exists: 'missing',
};

/** One-line summary of a stored filter, e.g. "organization_location not in CL, MX". */
export function formatTriggerFilter(key: string, v: unknown): string {
  const [row] = filtersToRows({ [key]: v });
  const op = SHORT_OP[row.op] ?? row.op;
  return VALUE_FREE_FILTER_OPS.has(row.op) ? `${key} ${op}` : `${key} ${op} ${row.value}`;
}
